import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.scss';
import {
  FaBars,
  FaTimes,
  FaMapMarkedAlt,
  FaFileAlt,
  FaCogs,
  FaUser,
  FaSignOutAlt,
  FaMoneyBillAlt,
  FaTools
} from 'react-icons/fa';
import api from '../Axios/axios';
import { logoutSession } from '../../utils/session';
import navbarCraneLogo from '../../assets/navbar-crane-logo.png';

const toBool = (v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userCompanyId, setUserCompanyId] = useState('');
  const [hasTowerCrane, setHasTowerCrane] = useState(false);
  const [rentTowerCrane, setRentTowerCrane] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleDropdown = (name) =>
    setDropdownOpen(dropdownOpen === name ? null : name);

  const handleLogout = async () => {
    await logoutSession();
    navigate('/');
  };

  const getUserFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      setUserCompanyId(decoded.CompanyId);
      const roleClaim =
        decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      setUserRole(roleClaim);
      setIsSuperAdmin(decoded.IsSuperAdmin === true || decoded.IsSuperAdmin === 'true' || roleClaim === 'SuperAdmin');

      if (roleClaim === 'Owner' || roleClaim === 'Employee') {
        setHasTowerCrane(false);
        setRentTowerCrane(false);
      } else {
        if (typeof decoded.hasTowerCrane !== 'undefined') {
          setHasTowerCrane(toBool(decoded.hasTowerCrane));
        }
        if (typeof decoded.rentTowerCrane !== 'undefined') {
          setRentTowerCrane(toBool(decoded.rentTowerCrane));
        }
      }
    } catch (e) {
      console.error('Token decode failed', e);
    }
  };

  useEffect(() => {
    getUserFromToken();

    const fetchCompanyData = async () => {
      if (!userCompanyId || userRole !== 'Company') return;

      try {
        const token = localStorage.getItem('token');
        const res = await api.get(`/Company/GetById?id=${userCompanyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res?.data?.data || {};
        setHasTowerCrane(toBool(data.hasTowerCrane));
        setRentTowerCrane(toBool(data.rentTowerCrane));
      } catch (e) {
        console.error('Company data error:', e);
      }
    };

    fetchCompanyData();
  }, [userCompanyId, userRole]);

  const homePath = '/home';

  const isOwnerOrEmployee = userRole === 'Owner' || userRole === 'Employee';
  const isCompany = userRole === 'Company';
  const isAccountant = userRole === 'Accountant';
  const isRentalAccountant = userRole === 'RentalAccountant';
  const isRentalPersonnel = userRole === 'RentalPersonnel';
  const isTechnicalServiceAccountant = userRole === 'TechnicalServiceAccountant';
  const isOwner = userRole === 'Owner' || userRole === 'SuperAdmin';
  const isTechnicalManager = userRole === 'TechnicalServiceManager';
  const isTechnicalPersonnel = userRole === 'TechnicalServicePersonnel';

  const canSeeTechnicalService =
    isOwner || isCompany || isTechnicalManager || isTechnicalPersonnel || isTechnicalServiceAccountant;

  const technicalServicePath = useMemo(() => {
    if (isTechnicalPersonnel) return '/technical-service/my-jobs';
    if (isTechnicalManager) return '/technical-service';
    if (isCompany) return '/technical-service/request';
    if (isTechnicalServiceAccountant) return '/technical-service/archive';
    return '/technical-service/admin';
  }, [isTechnicalPersonnel, isTechnicalManager, isCompany, isTechnicalServiceAccountant]);

  const showTrackingMenu = useMemo(() => {
    if (userRole === 'Partner') return false;
    if (isTechnicalPersonnel) return false;
    if (isOwnerOrEmployee || isRentalPersonnel) return true;
    if (isCompany) {
      return hasTowerCrane || rentTowerCrane;
    }
    return true;
  }, [userRole, isTechnicalPersonnel, isOwnerOrEmployee, isRentalPersonnel, isCompany, hasTowerCrane, rentTowerCrane]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link
          to={homePath}
          className="navbar-logo"
          onClick={() => setIsOpen(false)}
        >
          <span className="navbar-logo-text">Star Crane</span>
          <img className="navbar-crane-logo" src={navbarCraneLogo} alt="" aria-hidden="true" />
        </Link>

        <div className="menu-icon" onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </div>

        <div className={`nav-menu ${isOpen ? 'active' : ''}`}>
          {showTrackingMenu && (
            <Link
              to="/grouptracking"
              className="nav-link"
              onClick={() => setIsOpen(false)}
            >
              Kule Vinçlerim <FaMapMarkedAlt style={{ marginLeft: 8 }} />
            </Link>
          )}

          {(userRole === 'Owner' || userRole === 'Employee' || isRentalPersonnel) && (
            <div
              className="dropdown"
              onMouseEnter={() => setDropdownOpen('offer')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="dropbtn" onClick={() => toggleDropdown('offer')}>
                Teklif <FaFileAlt style={{ marginLeft: 8 }} />
              </button>
              <div className={`dropdown-content ${dropdownOpen === 'offer' ? 'show' : ''}`}>
                <Link to="/offerpage" onClick={() => setIsOpen(false)}>
                  Kiralık Teklifi Gönder
                </Link>
                <Link to="/offerlist" onClick={() => setIsOpen(false)}>
                  Gönderilen Teklifler
                </Link>
                <Link to="/sellofferpage" onClick={() => setIsOpen(false)}>
                  Satış Teklifi Gönder
                </Link>
              </div>
            </div>
          )}

          {(userRole === 'Owner' || userRole === 'Employee' || isRentalPersonnel) && (
            <div
              className="dropdown"
              onMouseEnter={() => setDropdownOpen('crane')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="dropbtn" onClick={() => toggleDropdown('crane')}>
                Kule Vinç İşlemleri <FaCogs style={{ marginLeft: 8 }} />
              </button>
              <div className={`dropdown-content ${dropdownOpen === 'crane' ? 'show' : ''}`}>
                {(userRole === 'Owner' || userRole === 'Employee' || isRentalPersonnel) && (
                  <Link to="/add-crane" onClick={() => setIsOpen(false)}>
                    Vinç Ekle
                  </Link>
                )}
                <Link to="/cranerental" onClick={() => setIsOpen(false)}>
                  Vinç Kiralama
                </Link>
                {userRole === 'Owner' && (
                  <Link to="/towercraneupdate" onClick={() => setIsOpen(false)}>
                    Kule Vinç Güncelleme
                  </Link>
                )}
              </div>
            </div>
          )}

          {(userRole === 'Owner' || userRole === 'Employee') && (
            <div
              className="dropdown"
              onMouseEnter={() => setDropdownOpen('statistics')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="dropbtn" onClick={() => toggleDropdown('statistics')}>
                İstatistikler <FaCogs style={{ marginLeft: 8 }} />
              </button>
              <div className={`dropdown-content ${dropdownOpen === 'statistics' ? 'show' : ''}`}>
                <Link to="/machinestats2" onClick={() => setIsOpen(false)}>
                  Makine İstatistikleri
                </Link>
                <Link to="/machinestats" onClick={() => setIsOpen(false)}>
                  Makine İstatistikleri (Eski)
                </Link>
              </div>
            </div>
          )}

          {(userRole === 'Owner' || userRole === 'Employee' || isRentalPersonnel) && (
            <div
              className="dropdown"
              onMouseEnter={() => setDropdownOpen('admin')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="dropbtn" onClick={() => toggleDropdown('admin')}>
                Yönetim <FaCogs style={{ marginLeft: 8 }} />
              </button>
              <div className={`dropdown-content ${dropdownOpen === 'admin' ? 'show' : ''}`}>
                {isSuperAdmin && <Link to="/activity-log" onClick={() => { setIsOpen(false); setDropdownOpen(null); }}>İşlem Geçmişi</Link>}
                {(userRole === 'Owner' || userRole === 'Employee') && (
                  <>
                    <Link to="/user-list" onClick={() => setIsOpen(false)}>
                      Kullanıcı Ayarları
                    </Link>
                    <hr />
                  </>
                )}
                <Link to="/company-list" onClick={() => setIsOpen(false)}>
                  Şirket Listesi
                </Link>
                <Link to="/add-company" onClick={() => setIsOpen(false)}>
                  Şirket Ekle
                </Link>
              </div>
            </div>
          )}

          {(isAccountant || isRentalAccountant || isOwner) && (
            <div
              className="dropdown"
              onMouseEnter={() => toggleDropdown('accounting')}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button className="dropbtn" onClick={() => toggleDropdown('accounting')}>
                Muhasebe <FaMoneyBillAlt style={{ marginLeft: 8 }} />
              </button>
              <div className={`dropdown-content ${dropdownOpen === 'accounting' ? 'show' : ''}`}>
                <Link to="/accounting" onClick={() => setIsOpen(false)}>
                  Muhasebe Paneli
                </Link>
                <Link to="/accounting/invoice-drafts" onClick={() => setIsOpen(false)}>
                  Fatura İşlemleri
                </Link>
                {(isOwner || isAccountant || isRentalAccountant) && (
                  <Link to="/accounting/manual-invoice" onClick={() => setIsOpen(false)}>
                    Manuel Fatura Oluştur
                  </Link>
                )}
                {isOwner && (
                  <>
                    <hr />
                    <Link to="/settings/nette-fatura" onClick={() => setIsOpen(false)}>
                      NetteFatura Hesapları
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}

          {canSeeTechnicalService && (
            <Link
              to={technicalServicePath}
              className="nav-link"
              onClick={() => setIsOpen(false)}
            >
              Teknik Servis <FaTools style={{ marginLeft: 8 }} />
            </Link>
          )}

          {(isAccountant || isRentalAccountant || isRentalPersonnel || isOwner) && (
            <Link
              to="/rental/archive"
              className="nav-link"
              onClick={() => setIsOpen(false)}
            >
              Kiralama Takip <FaFileAlt style={{ marginLeft: 8 }} />
            </Link>
          )}

          <div
            className="dropdown profile-dropdown"
          >
            <button className="dropbtn" onClick={() => toggleDropdown('profile')}>
              Profil <FaUser style={{ marginLeft: 8 }} />
            </button>
            <div className={`dropdown-content profile-menu ${dropdownOpen === 'profile' ? 'show' : ''}`}>
              <Link to="/profile" onClick={() => setIsOpen(false)}>
                Profil Düzenle
              </Link>
              <button className="dropdown-action logout-action" type="button" onClick={handleLogout}>
                <FaSignOutAlt /> Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
